<template>
  <header
    class="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 py-4 sm:px-6 lg:px-8"
    role="banner"
  >
    <div class="max-w-7xl mx-auto h-16 flex items-center justify-between gap-4">
      <h2 class="min-w-0 truncate text-xl font-bold font-condensed tracking-tight text-slate-900 dark:text-slate-100 sm:text-2xl" :title="name || 'Store'">
        {{ name || 'Store' }}
      </h2>
      <nav class="hidden md:flex items-center gap-6 lg:gap-8 text-sm font-medium" aria-label="Main">
        <a class="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded" :style="{ '--tw-ring-color': primaryColor || '#2563eb' }" href="#menu">{{ navLabel || viewListLabel }}</a>
        <a v-if="feedbackAvailable" class="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded" :style="{ '--tw-ring-color': primaryColor || '#2563eb' }" href="#reviews">{{ $t('public.reviewsAndFeedback') }}</a>
        <div v-else-if="isOwnerViewer && ownerViewMode" class="py-2 px-3 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20 max-w-[140px]">
          <p class="text-xs font-mono text-slate-600 dark:text-slate-400 break-words whitespace-normal">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
        </div>
        <a class="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded" :style="{ '--tw-ring-color': primaryColor || '#2563eb' }" href="#contact">{{ $t('public.contactUs') }}</a>
        <PublicReservationTrigger
          v-if="reservationAvailable"
          :slug="slug"
          template="template-5"
          :primary-color="primaryColor"
          :button-label="reserveButtonLabel"
          :modal-title="reserveModalHeading"
          :pax-label="reservePaxLabel"
        >
          <template #button="{ openModal }">
            <button
              type="button"
              class="min-h-[44px] px-5 py-2 rounded-lg font-bold text-sm text-white hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              :style="{ backgroundColor: primaryColor || '#2563eb' }"
              :aria-label="reserveButtonLabel || $t('reservation.reserveButton')"
              data-testid="reservation-open-modal"
              @click="openModal"
            >
              {{ reserveButtonLabel || $t('reservation.reserveButton') }}
            </button>
          </template>
        </PublicReservationTrigger>
        <div
          v-else-if="isOwnerViewer && ownerViewMode"
          class="py-2 px-3 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20 max-w-[140px]"
        >
          <p class="text-xs font-mono text-slate-600 dark:text-slate-400 break-words whitespace-normal">{{ $t('public.noBookingAvailableOwnerMessage') }}</p>
        </div>
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600"
          @select-locale="$emit('select-locale', $event)"
        />
      </nav>
      <div class="flex shrink-0 items-center gap-2 md:hidden">
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600"
          @select-locale="$emit('select-locale', $event)"
        />
        <button
          type="button"
          class="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-600 dark:text-slate-400 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          :style="{ '--tw-ring-color': primaryColor || '#2563eb' }"
          :aria-label="mobileNavOpen ? $t('app.close') : (viewListAriaLabel || $t('public.viewMenu'))"
          :aria-expanded="mobileNavOpen"
          data-testid="header-mobile-menu-btn"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <span class="material-symbols-outlined text-3xl">{{ mobileNavOpen ? 'close' : 'menu' }}</span>
        </button>
      </div>
    </div>
    <Transition name="rms-mobile-nav">
      <div
        v-if="mobileNavOpen"
        class="fixed inset-0 top-16 z-40 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        <div class="absolute inset-0 bg-black/50" aria-hidden="true" @click="mobileNavOpen = false" />
        <nav class="absolute left-0 right-0 top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-lg flex flex-col py-4 px-4 gap-1" aria-label="Main">
          <a class="text-base font-medium min-h-[44px] flex items-center px-4 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" :style="{ '--tw-ring-color': primaryColor || '#2563eb' }" href="#menu" @click="mobileNavOpen = false">{{ navLabel || viewListLabel }}</a>
          <a v-if="feedbackAvailable" class="text-base font-medium min-h-[44px] flex items-center px-4 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" :style="{ '--tw-ring-color': primaryColor || '#2563eb' }" href="#reviews" @click="mobileNavOpen = false">{{ $t('public.reviewsAndFeedback') }}</a>
          <div v-else-if="isOwnerViewer && ownerViewMode" class="py-3 px-3 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20" @click="mobileNavOpen = false">
            <p class="text-xs font-mono text-slate-600 dark:text-slate-400">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
          </div>
          <a class="text-base font-medium min-h-[44px] flex items-center px-4 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" :style="{ '--tw-ring-color': primaryColor || '#2563eb' }" href="#contact" @click="mobileNavOpen = false">{{ $t('public.contactUs') }}</a>
          <PublicReservationTrigger
            v-if="reservationAvailable"
            :slug="slug"
            template="template-5"
            :primary-color="primaryColor"
            :button-label="reserveButtonLabel"
            :modal-title="reserveModalHeading"
            :pax-label="reservePaxLabel"
          >
            <template #button="{ openModal }">
              <button
                type="button"
                class="w-full text-left text-base font-bold min-h-[44px] flex items-center px-4 rounded-lg text-white hover:opacity-90"
                :style="{ backgroundColor: primaryColor || '#2563eb' }"
                :aria-label="reserveButtonLabel || $t('reservation.reserveButton')"
                data-testid="reservation-open-modal-mobile"
                @click="openModal()"
              >
                {{ reserveButtonLabel || $t('reservation.reserveButton') }}
              </button>
            </template>
          </PublicReservationTrigger>
          <div
            v-else-if="isOwnerViewer && ownerViewMode"
            class="py-3 px-3 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20"
            @click="mobileNavOpen = false"
          >
            <p class="text-xs font-mono text-slate-600 dark:text-slate-400">{{ $t('public.noBookingAvailableOwnerMessage') }}</p>
          </div>
        </nav>
      </div>
    </Transition>
    <div
      v-if="showOwnerNeedsDataNotice"
      class="max-w-7xl mx-auto px-4 sm:px-6 py-3"
      role="status"
      aria-live="polite"
    >
      <div class="py-4 px-4 rounded border border-dashed border-slate-300 dark:border-slate-600 bg-slate-100/80 dark:bg-slate-800/50">
        <p class="text-xs font-mono text-slate-600 dark:text-slate-400">{{ $t('public.ownerNeedsDataIntro') }}</p>
        <a
          v-if="ownerAdminUrl"
          :href="ownerAdminUrl"
          class="mt-2 inline-flex items-center text-xs font-mono underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded min-h-[44px]"
          :style="{ color: primaryColor || '#2563eb' }"
          :aria-label="$t('public.openAdminPage')"
        >
          {{ $t('public.openAdminPage') }}
        </a>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import LanguageDropdown from '@/components/public/LanguageDropdown.vue'
import PublicReservationTrigger from '@/components/public/PublicReservationTrigger.vue'

const mobileNavOpen = ref(false)

const props = defineProps({
  name: { type: String, default: '' },
  viewer: { type: Object, default: () => ({ is_owner: false, owner_admin_url: null, needs_data: false }) },
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
  reservationAvailable: { type: Boolean, default: false },
  slug: { type: String, default: '' },
  primaryColor: { type: String, default: null },
  viewListLabel: { type: String, default: '' },
  navLabel: { type: String, default: '' },
  viewListAriaLabel: { type: String, default: '' },
  reserveButtonLabel: { type: String, default: '' },
  reserveModalHeading: { type: String, default: '' },
  reservePaxLabel: { type: String, default: '' },
  feedbackAvailable: { type: Boolean, default: false },
})

const isOwnerViewer = computed(() => props.viewer?.is_owner === true)
const ownerAdminUrl = computed(() => {
  const url = props.viewer?.owner_admin_url
  return typeof url === 'string' && url.trim() !== '' ? url : null
})
const showOwnerNeedsDataNotice = computed(() =>
  props.ownerViewMode && isOwnerViewer.value && props.viewer?.needs_data === true
)

defineEmits(['select-locale'])
</script>

<style scoped>
.font-condensed {
  font-family: 'Roboto Condensed', ui-sans-serif, system-ui, sans-serif;
}
.rms-mobile-nav-enter-active,
.rms-mobile-nav-leave-active {
  transition: opacity 0.2s ease;
}
.rms-mobile-nav-enter-from,
.rms-mobile-nav-leave-to {
  opacity: 0;
}
</style>
